/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { utils, writeFileXLSX } from 'xlsx'

export enum PageStatusEnum {
  active = 'Ativo',
  inactive = 'Inativo',
  deleted = 'Excluído'
}

export enum LandingPageStatusEnum {
  draft = 'Rascunho',
  published = 'Publicado',
  scheduled = 'Agendado',
  archived = 'Arquivado',
}

export enum ProductStatusEnum {
  active = 'Ativo',
  inactive = 'Inativo',
  deleted = 'Excluído'
}

export enum UserRoleEnum {
  customer = 'Cliente',
  fiscal = 'Fiscal',
  editor = 'Editor',
  admin = 'Administrador',
  physical_store_manager = 'Gerente de Loja Física',
  human_resources = 'Recursos Humano',
}

export enum ColorsVariants {
  active = 'rgba(0, 120, 212, 1)',
  deleted = 'rgba(220, 53, 69, 1)',
  inactive = 'rgba(108, 117, 125, 1)',
  password_recovery = 'rgba(255, 193, 7, 1)',
  blocked = 'rgba(40, 167, 69, 1)',
  delete_pending = 'rgba(108, 117, 125, 2)',
  confirmation_pending = 'rgba(23, 162, 184, 1)',
  draft = 'rgba(108, 117, 125, 3)',
}

export enum UserStatusEnum {
  confirmation_pending = 'Aguardando confirmação',
  active = 'Ativo',
  inactive = 'Inativo',
  blocked = 'Bloqueado',
  password_recovery = 'Recuperação de senha',
  deleted = 'Deletado',
  delete_pending = 'Aguardando exclusão'
}

export enum StoreRegionEnum {
  west_zone = 'Oeste',
  south_zone = 'Sul',
  east_zone = 'Zona Leste',
  center = 'Centro',
  north_zone = 'Norte',
  virtual = 'Virtual',
  unknown = 'Desconhecido',
  niteroi = 'Niterói',
}

export enum StoreStatusEnum {
  active = 'Ativo',
  inactive = 'Inativo',
  deleted = 'Excluído',
  under_renovation = 'Em reforma'
}

export enum RecipeStatusEnum {
  draft = 'Rascunho',
  active = 'Ativo',
  deleted = 'Excluído',
  inactive = 'Inativo'
}

export enum RecipeTypeEnum {
  Recipe = 'Receita',
  Tip = 'Dica'
}

export enum RecipeDifficultyEnum {
  easy = 'Fácil',
  medium = 'Média',
  hard = 'Difícil'
}

export async function getBase64fromFile (file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onerror = reject
    reader.onload = () => {
      resolve(reader.result as string)
    }
  })
}

export function getDiffFromObject(one: { [key: string]: any }, two: { [key: string]: any }) {
  return Object.keys(two).reduce((diff, key) => (one[key] === two[key]) ? diff : { ...diff, [key]: two[key] }, {})
}

export function slugify  (text: string): string {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
}

export const handleDownloadExcel = (
  dataSource: { [key:PropertyKey]: any }[],
  sheetName: string,
  fileName: string
) => {
  const worksheet = utils.json_to_sheet(dataSource)
  const workbook = utils.book_new()
  utils.book_append_sheet(workbook, worksheet, sheetName)
  writeFileXLSX(workbook, `${fileName}.xlsx`)
}

export function downloadBase64File (filename: string, mimeType: string, contentBase64: string) {
  const bytes = Uint8Array.from(atob(contentBase64), char => char.charCodeAt(0))
  const blob = new Blob([bytes], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
